// Bell-icon notification dropdown — top-right of the header.
//
// Polls /api/notifications/count every 60s for the badge count and lazy-loads
// the dropdown list on open. Clicking a notification marks it read, closes
// the panel, and navigates to its `link` via the Tabs system (so it slots
// into the existing workspace-tab UX instead of doing a hard route change).

import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useTabs } from "@/contexts/TabsContext";
import { Bell, CheckCheck, HelpCircle, MessageSquareReply, Inbox, AlertTriangle, AlertOctagon, ClipboardList, CheckCircle2, XCircle } from "lucide-react";

const POLL_MS = 60000;

const TYPE_META = {
  query_raised:     { icon: HelpCircle,        accent: "text-amber-700",   pill: "bg-amber-100 text-amber-800" },
  query_answered:   { icon: MessageSquareReply, accent: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800" },
  approval_pending: { icon: Inbox,              accent: "text-blue-700",    pill: "bg-blue-100 text-blue-800" },
  budget_warning:   { icon: AlertTriangle,      accent: "text-amber-700",   pill: "bg-amber-100 text-amber-800" },
  budget_critical:  { icon: AlertTriangle,      accent: "text-rose-700",    pill: "bg-rose-100 text-rose-800" },
  budget_breach:    { icon: AlertOctagon,       accent: "text-rose-900",    pill: "bg-rose-200 text-rose-900" },
  // Sprint 2026-Feb — Phase 3 ONE (Official Note Entry) module.
  // Same visual language as the corresponding approval / query / final
  // events but with a ClipboardList icon so the ONE origin is immediately
  // recognisable in the bell dropdown.
  one_approval_pending: { icon: ClipboardList,       accent: "text-blue-700",    pill: "bg-blue-100 text-blue-800" },
  one_query_raised:     { icon: HelpCircle,          accent: "text-amber-700",   pill: "bg-amber-100 text-amber-800" },
  one_query_replied:    { icon: MessageSquareReply,  accent: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800" },
  one_approved:         { icon: CheckCircle2,        accent: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800" },
  one_rejected:         { icon: XCircle,             accent: "text-rose-700",    pill: "bg-rose-100 text-rose-800" },
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);
  const { openTab } = useTabs();

  const fetchCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/count");
      setUnread(Number(r.data?.unread || 0));
    } catch { /* silent — keep last known count */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      setItems(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, [fetchCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  async function handleClick(n) {
    if (!n.read) {
      try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) openTab(n.link);
  }

  async function markAllRead() {
    try {
      const r = await api.post("/notifications/mark-read", { all: true });
      setUnread(0);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch { /* ignore */ }
  }

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={toggle}
        data-testid="notification-bell"
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "Notifications"}
        className="relative w-9 h-9 inline-flex items-center justify-center border border-slate-200 bg-white hover:border-[#F97316] hover:text-[#F97316] text-slate-700"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-count"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#F97316] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[360px] sm:w-[420px] max-h-[480px] bg-white border border-slate-200 shadow-lg z-50 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Notifications</div>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-[11px] text-[#F97316] hover:underline flex items-center gap-1"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-xs text-slate-500 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-xs text-slate-500 text-center" data-testid="notification-empty">
                You're all caught up.
              </div>
            ) : (
              items.map((n) => {
                const M = TYPE_META[n.type] || TYPE_META.query_raised;
                const Icon = M.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 ${
                      n.read ? "bg-white" : "bg-orange-50/40"
                    }`}
                  >
                    <div className={`w-7 h-7 shrink-0 inline-flex items-center justify-center ${M.pill}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-xs font-bold ${n.read ? "text-slate-700" : "text-slate-900"} break-words`}>
                          {n.title}
                        </div>
                        {!n.read && <span className="w-2 h-2 bg-[#F97316] rounded-full mt-1 shrink-0" />}
                      </div>
                      {n.message && (
                        <div className={`text-[11px] mt-0.5 break-words ${n.read ? "text-slate-500" : "text-slate-700"}`}>
                          {n.message.length > 140 ? n.message.slice(0, 140) + "…" : n.message}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1">{relTime(n.created_at)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
